import { useState } from "react";
import { Link } from "react-router-dom";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import vw from "../../api/virtualWorld.api";
import PlayerCard from "../../components/VirtualWorld/PlayerCard";
import ModeTile from "../../components/VirtualWorld/ModeTile";
import StatPill from "../../components/VirtualWorld/StatPill";
import Loading from "../../components/Loading";

const cr = (n) => `${Number(n || 0).toLocaleString("de-DE")} €`;

const Progress = ({ value }) => (
  <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
    <div className="h-full bg-green-500" style={{ width: `${Math.max(0, Math.min(100, value || 0))}%` }} />
  </div>
);

const ToneBadge = ({ tone }) => {
  const map = {
    hot: "bg-yellow-100 text-yellow-900 border-yellow-400",
    good: "bg-emerald-100 text-emerald-900 border-emerald-400",
    work: "bg-orange-100 text-orange-900 border-orange-400",
    neutral: "bg-gray-100 text-gray-900 border-gray-300",
  };
  return (
    <span className={`px-2 py-1 rounded-full border text-xs font-bold ${map[tone] || map.neutral}`}>
      {tone || "neutral"}
    </span>
  );
};

function fridayLabel(ft) {
  if (!ft) return "—";
  if (ft.error) return "API-Fehler";
  if (ft.nextRound) return ft.nextRound;
  if (ft.status === "open") return `Warteraum ${ft.humanCount || 0}/${ft.fieldSize || 8}`;
  if (ft.status === "running") return "läuft";
  if (ft.entered) return "im Feld";
  return "8er Freitag";
}

const CareerHome = () => {
  const { userId, career, loading, error, refresh } = useVirtualWorld();
  const [busy, setBusy] = useState(false);

  if (loading) return <Loading />;
  if (error) return <p className="text-red-600">{error}</p>;
  if (!career) return null;

  const start = async () => {
    setBusy(true);
    try {
      // Immer Agent zuerst — Umschalter auf manuell in den Settings
      await vw.enableCareer(userId, "agent");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  if (!career.enabled) {
    return (
      <div className="max-w-xl bg-white dark:bg-gray-700 rounded-2xl p-6 shadow border border-gray-200 dark:border-gray-600">
        <div className="text-4xl mb-3" aria-hidden="true">
          🎯
        </div>
        <h2 className="text-2xl font-bold mb-3">Virtuelle Karriere starten</h2>
        <p className="mb-4 opacity-80">
          Dein FightClub-Avatar (ggf. aus dem FlowLiga-Spiegel) sucht einen Club und kann freitags im 8er spielen.
          Start ist immer <strong>passiv mit Agent</strong> — manuell schaltest du später in den Settings um.
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={() => start()}
          className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white border border-yellow-400 rounded-xl font-semibold"
        >
          🚀 Karriere mit Agent starten
        </button>
      </div>
    );
  }

  const card = career.card;
  const offers = career.offers || [];
  const cockpit = career.cockpit || {};
  const goals = cockpit.goals || [];
  const agent = cockpit.agent || {};
  const unlocks = career.progression?.career?.unlocks || {};
  const next = career.progression?.career?.next;
  const early = !unlocks.contract;
  const bankroll = Number(career.bankroll ?? career.fridayTournament?.bankroll) || 0;
  const ft = career.fridayTournament;
  const statusPerks = career.statusPerks;
  const ladder = statusPerks?.ladder || [];
  const currentTier = ladder.find((t) => t.isCurrent) || null;
  const controlMode = career.controlMode || (career.transferMode === "manual" ? "active" : "agent");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span
          className={`px-3 py-1 rounded-full border font-semibold ${
            controlMode === "agent"
              ? "bg-emerald-50 border-emerald-400 text-emerald-900"
              : "bg-amber-50 border-amber-400 text-amber-900"
          }`}
        >
          {controlMode === "agent" ? "🤵 Agent (passiv)" : "✋ Manuell (aktiv)"}
        </span>
        <Link to="/career/settings" className="underline opacity-70 hover:opacity-100">
          Steuerung wechseln
        </Link>
      </div>
      {/* Immer sichtbar: Kasse + Freitag-Turnier */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-2xl border-2 border-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 p-5">
          <div className="text-xs uppercase tracking-wide opacity-60 mb-1">Bankroll</div>
          <div className="text-3xl font-black tabular-nums">{cr(bankroll)}</div>
          <p className="text-xs opacity-70 mt-1">Persönliche Kasse fürs Freitag-Turnier (Startgeld / Preise).</p>
        </div>
        <Link
          to="/career/tournament"
          className="block rounded-2xl border-2 border-amber-400 bg-gray-900 text-white p-5 hover:ring-2 hover:ring-amber-300 transition"
        >
          <div className="text-xs uppercase tracking-wide text-amber-200 mb-1">Freitag · Shared 8er</div>
          <div className="text-2xl font-black">{fridayLabel(ft)}</div>
          <p className="text-sm opacity-80 mt-1">
            {ft?.canEnter
              ? `Anmeldung offen · Startgeld ${cr(ft.entryFee || statusPerks?.fridayEntryFee || 400)}`
              : ft?.status === "open"
                ? `${ft.humanCount || 0}/${ft.fieldSize || 8} Menschen · KI füllt auf`
                : "Einzelturnier mit anderen Career-Spielern — hier öffnen"}
          </p>
        </Link>
      </div>

      {statusPerks ? (
        <div className="rounded-2xl border-2 border-indigo-400 bg-white dark:bg-gray-700 p-5 shadow">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-xs uppercase tracking-wide opacity-60">XP-Status · verdiente Rechte</div>
              <h2 className="text-2xl font-black">
                {statusPerks.statusLabel}
                {statusPerks.title ? (
                  <span className="text-indigo-600 dark:text-indigo-300"> · {statusPerks.title}</span>
                ) : null}
              </h2>
              <p className="text-sm opacity-80 mt-1 max-w-2xl">{statusPerks.blurb}</p>
            </div>
            <div className="text-right text-sm">
              <div className="font-bold tabular-nums">
                {(career.fightclubXp || 0).toLocaleString("de-DE")} XP
              </div>
              {currentTier && currentTier.progress < 100 ? (
                <div className="opacity-70">nächste Stufe {currentTier.progress}%</div>
              ) : null}
            </div>
          </div>
          {currentTier && currentTier.progress < 100 ? (
            <div className="mb-4">
              <Progress value={currentTier.progress} />
            </div>
          ) : null}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {ladder.map((tier) => (
              <div
                key={tier.key}
                className={`rounded-xl border p-3 text-xs ${
                  tier.isCurrent
                    ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40"
                    : tier.unlocked
                      ? "border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20"
                      : "border-gray-200 dark:border-gray-600 opacity-60"
                }`}
              >
                <div className="font-black text-sm">{tier.label}</div>
                <div className="opacity-70 tabular-nums">{tier.xpRequired.toLocaleString("de-DE")} XP</div>
                {tier.title ? <div className="mt-1 font-semibold">{tier.title}</div> : null}
                <ul className="mt-2 space-y-0.5 opacity-80">
                  {(tier.unlockHints || []).slice(0, 2).map((h) => (
                    <li key={h}>· {h}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {next && next.key !== "home" ? (
        <Link
          to={next.to || "/career/offers"}
          className="block w-full rounded-2xl border-2 border-yellow-400 bg-green-600 text-white p-5 shadow-md hover:bg-green-500 transition"
        >
          <div className="text-xs uppercase tracking-widest text-yellow-100 mb-1">Nächster Schritt</div>
          <div className="text-2xl font-black">{next.title}</div>
          {next.hint ? <p className="text-sm mt-1 text-white/95">{next.hint}</p> : null}
        </Link>
      ) : null}

      {early ? (
        <div className="max-w-2xl bg-white dark:bg-gray-700 rounded-2xl p-6 shadow border border-yellow-400 space-y-4">
          <h2 className="text-2xl font-bold">Einstieg: erstes Angebot</h2>
          <p className="opacity-80">
            Schau unter Angebote — Agent oder manueller Deal. Parallel kannst du schon das Freitag-8er spielen (Kasse
            oben).
          </p>
          {offers.length ? (
            <div className="rounded-xl bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-400 p-4 text-sm">
              <strong>{offers.length} offene Angebot{offers.length === 1 ? "" : "e"}</strong>
              <ul className="mt-2 space-y-1">
                {offers.slice(0, 3).map((o) => (
                  <li key={o.id}>
                    {o.clubName} · {o.role} · {cr(o.wage)}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm opacity-70">Noch kein Angebot — Agent arbeitet oder Posteingang gleich checken.</p>
          )}
          <PlayerCard player={card} />
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <StatPill emoji="🎭" label="Rolle" value={card.role || "Free Agent"} />
            <StatPill emoji="⭐" label="Career Score" value={cockpit.careerScore || "–"} />
            <StatPill emoji="💸" label="Marktwert" value={cr(cockpit.marketValue || card.marketValue)} />
            <StatPill emoji="📈" label="Trend" value={cockpit.trend?.label || "Debüt"} />
          </div>
          <div className="grid xl:grid-cols-[1.1fr_0.9fr] gap-6">
            <div className="space-y-6">
              <PlayerCard player={card} />
              <div className="bg-gray-900 text-white rounded-2xl p-6 shadow border border-yellow-400">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h2 className="text-2xl font-bold">{agent.title || "Agentenbriefing"}</h2>
                  <ToneBadge tone={agent.tone} />
                </div>
                <p className="text-lg">{agent.text || "Noch kein Briefing."}</p>
                <p className="text-sm text-yellow-200 mt-3">{agent.advice}</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-700 rounded-2xl p-6 shadow border border-gray-200 dark:border-gray-600">
                <h2 className="text-2xl font-bold mb-3">📋 Karriere-Cockpit</h2>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-gray-100 dark:bg-gray-800 p-3">
                    <div className="opacity-60">Saison</div>
                    <div className="font-bold">
                      {card.seasonWins || 0}–{card.seasonLosses || 0} · AVG {card.virtualAverage || "–"}
                    </div>
                  </div>
                  <div className="rounded-xl bg-gray-100 dark:bg-gray-800 p-3">
                    <div className="opacity-60">Nächstes Spiel</div>
                    <div className="font-bold">{career.nextMatch || "frei"}</div>
                  </div>
                  <div className="rounded-xl bg-gray-100 dark:bg-gray-800 p-3">
                    <div className="opacity-60">Vertrag</div>
                    <div className="font-bold">
                      {career.contract
                        ? `${career.contract.clubName} · ${career.contract.role}`
                        : "Free Agent"}
                    </div>
                  </div>
                  <div className="rounded-xl bg-gray-100 dark:bg-gray-800 p-3">
                    <div className="opacity-60">FightClub</div>
                    <div className="font-bold">
                      AVG {career.fightclubAverage || "–"} · XP{" "}
                      {career.fightclubXp?.toLocaleString("de-DE")}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-700 rounded-2xl p-6 shadow border border-gray-200 dark:border-gray-600">
                <h2 className="text-2xl font-bold mb-4">🎯 Saisonziele</h2>
                <div className="space-y-4">
                  {goals.map((goal) => (
                    <div key={goal.key}>
                      <div className="flex justify-between gap-3 text-sm mb-1">
                        <span className="font-semibold">
                          {goal.done ? "✅ " : ""}
                          {goal.title}
                        </span>
                        <span className="opacity-70">
                          {goal.current}/{goal.target}
                        </span>
                      </div>
                      <Progress value={goal.progress} />
                      <p className="text-xs opacity-70 mt-1">{goal.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
              {cockpit.lastResults?.length ? (
                <div className="bg-white dark:bg-gray-700 rounded-2xl p-6 shadow border border-gray-200 dark:border-gray-600">
                  <h2 className="text-2xl font-bold mb-3">🧾 Letzte Auftritte</h2>
                  <div className="space-y-2 text-sm">
                    {cockpit.lastResults.map((r, i) => (
                      <div
                        key={`${r.legs}-${i}`}
                        className="flex justify-between rounded-lg bg-gray-100 dark:bg-gray-800 p-2"
                      >
                        <span className="font-bold">
                          {r.result} · {r.legs}
                        </span>
                        <span>
                          AVG {r.avg} · CO {r.checkout}% · 180er {r.max}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <ModeTile
          to="/career/tournament"
          emoji="🏅"
          title="Freitag-Turnier"
          hint={
            ft?.status === "open"
              ? `8er Warteraum ${ft.humanCount || 0}/${ft.fieldSize || 8} · Kasse ${cr(bankroll)}`
              : `Shared 8er · Kasse ${cr(bankroll)}`
          }
          featured={Boolean(ft?.canEnter || ft?.status === "open" || ft?.nextRound)}
        />
        <ModeTile
          to="/career/shop"
          emoji="🛒"
          title="Dart-Shop"
          hint={
            career.gear?.equippedName
              ? `Aktiv: ${career.gear.equippedName}`
              : `Sets + XP-Exklusiv · Kasse ${cr(bankroll)}`
          }
          featured={!career.gear?.equippedKey && bankroll >= 69}
        />
        <ModeTile
          to="/career/offers"
          emoji="📨"
          title="Angebote"
          hint={offers.length ? `${offers.length} offen` : "Posteingang"}
          featured={offers.length > 0 || early}
        />
        {unlocks.contract ? (
          <ModeTile to="/career/contract" emoji="📝" title="Vertrag" hint="Club und Gehalt" />
        ) : null}
        {unlocks.matches ? (
          <ModeTile to="/career/matches" emoji="🎯" title="Matches" hint="Virtuelle Ergebnisse" />
        ) : null}
        {unlocks.history ? (
          <ModeTile to="/career/history" emoji="📜" title="Historie" hint="Meilensteine" />
        ) : null}
      </div>
    </div>
  );
};

export default CareerHome;
